import { Action, configureStore, createListenerMiddleware, ThunkAction } from '@reduxjs/toolkit';
import reducer, { AppProjectEditorRootState } from './reducer';
import setupAppProjectEditorSideEffects from './setupAppProjectEditorSideEffects';

interface AppProjectEditorStoreOptions {
  /**
   * Whether or not to enable listener middleware side-effects.
   * @default true
   */
  enableSideEffects?: boolean;
  /**
   * Initial preloaded state for the Redux store.
   */
  preloadedState?: Partial<AppProjectEditorRootState>;
}

export function createAppProjectEditorStore({
  enableSideEffects = true,
  preloadedState,
}: AppProjectEditorStoreOptions = {}) {
  const listenerMiddleware = createListenerMiddleware<AppProjectEditorRootState>();
  if (enableSideEffects) {
    setupAppProjectEditorSideEffects(listenerMiddleware);
  }
  return configureStore({
    preloadedState,
    reducer,
    // Add custom middlewares
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        // While these two development-only middlewares would be useful, they are way
        // too heavy for large stores, i.e. ones holding a huge DOLAN app, causing seconds
        // of additional lag. They are disabled by default. For debugging, you may temporarily
        // comment the following lines.
        serializableCheck: false,
        immutableCheck: false,
      }).prepend(listenerMiddleware.middleware),
  });
}

export type AppProjectEditorStore = ReturnType<typeof createAppProjectEditorStore>;
export type AppProjectEditorDispatch = AppProjectEditorStore['dispatch'];
export type AppProjectEditorThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  AppProjectEditorRootState,
  unknown,
  Action<string>
>;
